import React, { useEffect, useState } from "react";
import {
  Tent, Megaphone, Sparkles, Users, Flame, Star, Bell, Calendar,
  MapPin, Clock, ChevronRight, Loader2,
} from "lucide-react";
import { Reveal, SectionHeading, TiltCard } from "./Atoms";
import { api } from "../../lib/api";

const EVENT_KINDS = [
  { icon: Tent, t: "Upcoming Tent Revivals", b: "Outdoor revival services calling cities back to Jesus." },
  { icon: Megaphone, t: "Crusades", b: "Mass evangelism events with preaching, prayer, and demonstration." },
  { icon: Flame, t: "Revival Services", b: "Holy Ghost services for repentance, healing, and fire." },
  { icon: Users, t: "Prayer Gatherings", b: "Corporate intercession and travail until heaven opens." },
  { icon: Sparkles, t: "Fire Hub Gatherings", b: "Regional altars of consecration, training, and impartation." },
  { icon: Star, t: "Special Events", b: "Conferences, schools of the prophets, and equipping nights." },
];

// Pick an icon based on title keywords
function iconFor(title = "") {
  const t = title.toLowerCase();
  if (t.includes("tent")) return Tent;
  if (t.includes("crusade")) return Megaphone;
  if (t.includes("prayer")) return Users;
  if (t.includes("fire hub") || t.includes("hub")) return Sparkles;
  if (t.includes("revival") || t.includes("service") || t.includes("sunday")) return Flame;
  return Star;
}

const DateBlock = ({ iso, allDay }) => {
  const d = new Date(iso);
  const month = d.toLocaleString(undefined, { month: "short" }).toUpperCase();
  const day = d.getDate();
  const weekday = d.toLocaleString(undefined, { weekday: "short" }).toUpperCase();
  return (
    <div className="flex flex-col items-center justify-center w-20 h-24 rounded-xl border border-[#C9A44C]/30 bg-[#0a0a0a] shrink-0">
      <div className="text-[10px] tracking-[0.22em] uppercase text-[#C9A44C]">{month}</div>
      <div className="font-serif-display text-4xl text-[#F6F0E6] leading-none mt-0.5">{day}</div>
      <div className="text-[10px] tracking-[0.18em] uppercase text-[#D9D9D9]/65 mt-1">{weekday}</div>
    </div>
  );
};

const fmtTimeRange = (startISO, endISO, allDay) => {
  if (allDay) return "All Day";
  const s = new Date(startISO);
  const e = endISO ? new Date(endISO) : null;
  const opts = { hour: "numeric", minute: "2-digit" };
  const sStr = s.toLocaleTimeString(undefined, opts);
  if (!e) return sStr;
  return `${sStr} – ${e.toLocaleTimeString(undefined, opts)}`;
};

// Extract first http(s) URL from description — used as "Join" CTA
function extractJoinLink(desc = "") {
  const m = desc.match(/https?:\/\/[^\s<>"]+/i);
  return m ? m[0] : null;
}
function isOnline(text = "") {
  return /zoom|meet\.google|teams\.microsoft|stream\.|youtube|facebook|twitch|live/i.test(text);
}
function stripUrls(text = "") {
  return text.replace(/https?:\/\/[^\s<>"]+/gi, "").trim();
}

const EventCard = ({ e }) => {
  const Icon = iconFor(e.title);
  const joinLink = extractJoinLink(e.description) || extractJoinLink(e.location);
  const online = isOnline(e.location) || isOnline(e.description);
  // Strip the URL from the visible description so it doesn't appear twice
  const cleanDesc = stripUrls(e.description || "");
  return (
    <TiltCard strength={2}>
      <article className="bento-card !p-6 h-full flex flex-col" data-testid={`live-event-${(e.uid || "evt").slice(0, 16)}`}>
        <span className="glow-blob" />
        <div className="flex items-start gap-4">
          <DateBlock iso={e.start_iso} allDay={e.all_day} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-[10px] tracking-[0.22em] uppercase text-[#C9A44C]">
              <Icon className="h-3.5 w-3.5" strokeWidth={1.6} />
              {online ? "Online Gathering" : "Revival Event"}
            </div>
            <h3 className="font-serif-display text-2xl text-[#F6F0E6] leading-tight mt-1 line-clamp-2">{e.title}</h3>
            <div className="mt-3 space-y-1.5 text-sm text-[#D9D9D9]">
              <div className="flex items-start gap-2"><Clock className="h-3.5 w-3.5 text-[#C9A44C] mt-0.5 shrink-0" />{fmtTimeRange(e.start_iso, e.end_iso, e.all_day)}</div>
              {e.location && !joinLink?.includes(e.location) && (
                <div className="flex items-start gap-2"><MapPin className="h-3.5 w-3.5 text-[#C9A44C] mt-0.5 shrink-0" /><span className="break-words">{e.location}</span></div>
              )}
            </div>
          </div>
        </div>
        {cleanDesc && (
          <p className="mt-4 text-[13.5px] text-[#D9D9D9]/85 leading-relaxed line-clamp-3">{cleanDesc}</p>
        )}
        <div className="mt-auto pt-5 flex flex-wrap items-center gap-2">
          {joinLink ? (
            <a href={joinLink} target="_blank" rel="noopener noreferrer" className="btn-fire !py-2.5 !px-4 !text-xs" data-testid={`join-${(e.uid || "evt").slice(0,8)}`}>
              <ChevronRight className="h-3 w-3" /> {online ? "Join Online" : "Get Details"}
            </a>
          ) : null}
          <a
            href={`https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(e.title)}&dates=${(e.start_iso || "").replace(/[-:]/g, "").slice(0,15)}Z/${(e.end_iso || e.start_iso || "").replace(/[-:]/g, "").slice(0,15)}Z&details=${encodeURIComponent(e.description || "")}&location=${encodeURIComponent(e.location || "")}`}
            target="_blank" rel="noopener noreferrer"
            className="btn-ghost !py-2.5 !px-4 !text-xs"
          >
            <Calendar className="h-3 w-3" /> Add to Calendar
          </a>
        </div>
      </article>
    </TiltCard>
  );
};

const Events = () => {
  const [data, setData] = useState({ configured: false, events: [], loading: true });

  useEffect(() => {
    api.get("/events/upcoming")
      .then((r) => setData({ ...r.data, loading: false }))
      .catch(() => setData({ configured: false, events: [], loading: false }));
  }, []);

  return (
    <section id="events" className="relative py-28 px-6 bg-[#050505]">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <SectionHeading
            overline="National Revival Mandate"
            title={<>Tent Revivals, Crusades & <span className="italic gold-text">Revival Gatherings</span></>}
            subtitle="God has commissioned Revival Fire Ministries Global to host tent revivals, crusades, gatherings, and revival events across the nation — calling cities and regions back to Jesus Christ through repentance, holiness, deliverance, healing, prayer, worship, the preaching of the Word, and the demonstration of the power of the Holy Ghost."
          />
        </Reveal>

        {/* Live upcoming events */}
        {data.loading ? (
          <Reveal>
            <div className="bento-card !p-10 text-center mb-12" data-testid="events-loading">
              <span className="glow-blob" />
              <Loader2 className="h-5 w-5 animate-spin text-[#C9A44C] mx-auto" />
              <div className="mt-3 text-sm text-[#D9D9D9]/70">Loading upcoming services…</div>
            </div>
          </Reveal>
        ) : data.configured && data.events.length > 0 ? (
          <Reveal>
            <div className="mb-14">
              <div className="flex items-center gap-2 mb-6">
                <Calendar className="h-4 w-4 text-[#C9A44C]" />
                <span className="overline">Upcoming · {data.events.length} gatherings</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="upcoming-events-grid">
                {data.events.map((e, i) => (
                  <Reveal key={e.uid || i} delay={i * 60}>
                    <EventCard e={e} />
                  </Reveal>
                ))}
              </div>
            </div>
          </Reveal>
        ) : data.configured && data.events.length === 0 ? (
          <Reveal>
            <div className="bento-card !p-10 text-center mb-12" data-testid="events-empty">
              <span className="glow-blob" />
              <Calendar className="h-6 w-6 text-[#C9A44C]/70 mx-auto" />
              <div className="font-serif-display text-2xl text-[#F6F0E6] mt-3">Next gathering is being prepared</div>
              <p className="text-sm text-[#D9D9D9]/70 mt-2 max-w-md mx-auto">Join updates below to be the first to know when the next tent revival, crusade, or prayer night is announced.</p>
            </div>
          </Reveal>
        ) : null}

        {/* Pillar event-types grid (always shown) */}
        <Reveal>
          <div className="mb-6 mt-2">
            <span className="overline">What we host</span>
            <h3 className="font-serif-display text-3xl text-[#F6F0E6] mt-1">Six altars. One mandate.</h3>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {EVENT_KINDS.map(({ icon: Icon, t, b }, i) => (
            <Reveal key={t} delay={i * 70}>
              <TiltCard strength={3}>
                <div className="bento-card h-full min-h-[170px]" data-testid={`event-${i}`}>
                  <span className="glow-blob" />
                  <Icon className="h-5 w-5 text-[#E8452C]" strokeWidth={1.5} />
                  <h3 className="mt-4 font-serif-display text-xl text-[#F6F0E6]">{t}</h3>
                  <p className="mt-1.5 text-[14px] text-[#D9D9D9] leading-relaxed">{b}</p>
                </div>
              </TiltCard>
            </Reveal>
          ))}
        </div>

        <Reveal delay={300}>
          <div className="mt-12">
            <a href="#updates" className="btn-fire" data-testid="events-cta">
              <Bell className="h-4 w-4" strokeWidth={1.7} />
              Join Updates for Event Announcements
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Events;
